<template>
  <ui-input
    v-bind="$attrs"
    v-on="$listeners"
    class="location-service-image"
  >
    <el-upload
      ref="upload"
      action="/api/v1/user/settings/image"
      :show-file-list="false"
      :auto-upload="false"
      :accept="accept"
      :on-change="handleSelect"
    >
      <div class="location-service-image__content">
        <img
          v-if="imagePreview"
          :src="imagePreview"
          class="location-service-image__img"
        />
        <div class="location-service-image__buttons">
          <ui-button
            native-type="button"
            size="sm"
            outline
            @click.stop="openSelect"
          >
            Upload image
          </ui-button>
          <ui-button
            v-if="imagePreview"
            native-type="button"
            size="sm"
            outline
            @click.stop="handleRemove"
          >
            Remove
          </ui-button>
        </div>
      </div>
      <template name="trigger">
        <button
          type="button"
          class="invisible location-service-image__trigger-btn"
          ref="open-file-dialog"
        ></button>
      </template>
    </el-upload>

    <cropper-modal
      v-model="showCropper"
      title="Upload image"
      :aspects="cropperAspects"
      :image="selectedImage"
      :type="selectedType"
      @apply="onApply"
      @closed="onClose"
    />
  </ui-input>
</template>

<script>
import { Upload } from 'element-ui';
import CropperModal from '@/components/CropperModal.vue';
import resetMixin from '@/mixins/reset-mixin';
import { MB } from '@/utils/bytes';

const allowTypes = [
  'image/jpg',
  'image/jpeg',
  'image/png',
];

export default {
  components: {
    [Upload.name]: Upload,
    CropperModal,
  },

  mixins: [
    resetMixin(() => ({
      showCropper: false,
      selectedImage: null,
      selectedType: null,
      croppedImage: null,
    })),
  ],

  props: {
    value: {
      type: null,
      required: true,
    },
  },

  data() {
    return {
      cropperAspects: [
        { aspectRatio: 1, label: 'Square' },
        { aspectRatio: 163 / 75, label: 'Rectangle' },
      ],
    };
  },

  computed: {
    imageMaxSizeMb() {
      return 5;
    },
    imageMaxSize() {
      return this.imageMaxSizeMb * MB;
    },
    imagePreview() {
      if (this.value) {
        return this.value instanceof Blob
          ? URL.createObjectURL(this.value)
          : this.value;
      }

      return null;
    },
    accept() {
      return allowTypes.join(', ');
    },
  },

  mounted() {
    this.reset();
  },

  methods: {
    openSelect() {
      this.$refs['open-file-dialog'].click();
    },
    handleSelect(file) {
      if (!allowTypes.includes(file.raw.type)) {
        this.$showServerError({
          message: `Available formats: ${allowTypes.map((v) => v.replace('image/', ''))}`,
        });
      } else if (file.size > this.imageMaxSize) {
        this.$showServerError({
          message: `Max image size: ${this.imageMaxSizeMb}MB`,
        });
      } else {
        this.selectedImage = URL.createObjectURL(file.raw);
        this.selectedType = file.raw.type;
        this.openCropper();
      }
    },
    handleRemove() {
      this.$emit('input', null);
      this.reset();
    },
    openCropper() {
      this.showCropper = true;
    },
    onApply(croppedFile) {
      this.$emit('input', croppedFile);
    },
    onClose() {
      this.reset();
    },
  },
};
</script>

<style lang="scss">
.design .location-service-image {
  position: relative;
  cursor: pointer;
  margin-top: 40px;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 10px;
  }

  &__img {
    width: 240px;
    border-radius: 10px;
  }

  &__buttons {
    display: flex;
    gap: 10px;
  }

  &__trigger-btn {
    // Safari iOS layout fix
    position: absolute;
  }

  .el-upload {
    width: 100%;
    height: 100%;
    display: flex;
  }
}
</style>
